type SentryClient = typeof import('@sentry/react');
type CaptureContext = Parameters<SentryClient['captureException']>[1];

let sentryClientPromise: Promise<SentryClient> | null = null;
let sentryInitPromise: Promise<SentryClient | null> | null = null;

const getTracePropagationTargets = () => {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  const targets: (string | RegExp)[] = ['localhost'];

  if (apiBaseUrl) {
    try {
      targets.push(new URL(apiBaseUrl).origin);
    } catch {
      // Ignore malformed configuration; Sentry can still initialize.
    }
  }

  return targets;
};

export const isSentryEnabled = () => {
  return !(import.meta.env.MODE === 'e2e' && import.meta.env.VITE_E2E_AUTH_BYPASS === 'true');
};

const loadSentryClient = async () => {
  if (!isSentryEnabled()) return null;

  try {
    sentryClientPromise ??= import('@sentry/react');
    return await sentryClientPromise;
  } catch {
    sentryClientPromise = null;
    return null;
  }
};

export const initializeSentry = async () => {
  if (!isSentryEnabled()) return null;

  sentryInitPromise ??= loadSentryClient()
    .then((Sentry) => {
      if (!Sentry) {
        // A failed SDK import must not permanently disable later reporting
        // when the page remains open. Retry only on a subsequent caller.
        sentryInitPromise = null;
        return null;
      }

      Sentry.init({
        environment: import.meta.env.VITE_ENV,
        dsn: 'https://59bc67c57ceef2311aea3c5489fa9424@o4504880189145088.ingest.us.sentry.io/4508887612325888',
        integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
        tracesSampleRate: 1.0,
        tracePropagationTargets: getTracePropagationTargets(),
        replaysSessionSampleRate: 0.01,
        replaysOnErrorSampleRate: 1.0,
      });

      return Sentry;
    })
    .catch(() => {
      sentryInitPromise = null;
      return null;
    });

  return sentryInitPromise;
};

export const setSentryUser = (userEmail?: string) => {
  if (!userEmail) return;

  void initializeSentry().then((Sentry) => {
    Sentry?.setUser({ email: userEmail });
  });
};

export const reportException = (exception: unknown, captureContext?: CaptureContext) => {
  void initializeSentry().then((Sentry) => {
    Sentry?.captureException(exception, captureContext);
  });
};

// Bound initialization as well as transport: a blocked SDK import must not
// prevent recovery navigation. This does not change normal event reporting.
export const reportExceptionBeforeNavigation = async (
  exception: unknown,
  captureContext?: CaptureContext,
  timeoutMs = 1_000,
) => {
  const deadline = Date.now() + timeoutMs;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      initializeSentry().then(async (Sentry) => {
        if (!Sentry) return;
        Sentry.captureException(exception, captureContext);
        await Sentry.flush(Math.max(1, deadline - Date.now()));
      }),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, timeoutMs);
      }),
    ]);
  } catch {
    // Telemetry failure must not turn into a second application failure.
  } finally {
    clearTimeout(timer);
  }
};
